import {
  SHDivider,
  SHStack,
  SHTextField,
  SHTypography,
} from "@components/design-systems";
import { SHNumericFieldV2 } from "@components/design-systems/sh-numeric-field-v2";
import {
  useUpdateModelsTab,
  useUpdateSectionData,
} from "@hooks/useInvestmentProduct";
import { HighlightType } from "@models/configuration";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import { InvestmentProductModelsDTO } from "@models/product/investment-product/entities/model";
import {
  InvestmentBannerStatus,
  InvestmentDataStatus,
} from "@models/product/investment-product/enums/status";
import { useTheme } from "@mui/material";
import { DEBOUNCE_DELAY } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/configs";
import {
  getFeaturePaths,
  getFeatureProductDataPaths,
  getModelPath,
} from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/utils";
import { EditProductDefaults } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";
import { toggleSaveDraftButtonDisabled } from "@redux/slices/product/investment-product";
import { RootState, useAppDispatch } from "@redux/store";
import { debounce, isEmpty, isEqual, toString } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useMount } from "react-use";
import { useUserPermissions } from "@hooks/userUserPermission";

export interface ModelsTitleTextFieldProps {
  selectedIndex: [number, number];
  feature: InvestmentProductConfigurationFeature;
}

export const ModelTitleTextField = ({
  selectedIndex,
  feature,
}: ModelsTitleTextFieldProps) => {
  const originalTitle = feature.productData?.value;

  /** Hook **/
  const {
    investmentProductUI: { isSaving },
    investmentProductBannerInfo,
  } = useSelector((state: RootState) => state.investmentProduct);
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const isPendingApproval = investmentProductBannerInfo?.some((banner) =>
    isEqual(banner.status, InvestmentBannerStatus.PendingApproval),
  );
  const { control, getValues, setValue } = useFormContext<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>();
  const { isSuperAdmin } = useUserPermissions();
  const [isMounting, setIsMounting] = useState(true);
  useMount(() => setIsMounting(false));
  /** Memo **/
  const modelFieldPath = useMemo(
    () => getModelPath(selectedIndex),
    [selectedIndex],
  );
  const handleUpdateModelsTab = useUpdateModelsTab();
  const watchedFormData = useWatch({
    control,
    name: "investmentProductModels",
  });
  const isAnySectionModified = useMemo(() => {
    const formData = getValues("investmentProductModels") ?? [];
    return formData.some((item) => item.isModified);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFormData]);

  const isAnySectionEdited = useMemo(() => {
    const formData = getValues("investmentProductModels") ?? [];
    return formData.some((item) => item.highlightType === HighlightType.Edited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFormData]);

  const isAnySectionMissingData = useMemo(() => {
    const formData = getValues("investmentProductModels") ?? [];

    return formData.some(
      (item) => item.dataStatus === InvestmentDataStatus.MissingData,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFormData]);

  const featureProductDataPath = useMemo(
    () => getFeatureProductDataPaths(selectedIndex, 0, 0),
    [selectedIndex],
  );
  const featurePath = useMemo(
    () => getFeaturePaths(selectedIndex, 0, 0),
    [selectedIndex],
  );
  const handleUpdateSectionData = useUpdateSectionData(selectedIndex, 0);
  /** Handler **/
  const handleModelTitleChange = debounce((newValue: string) => {
    const publishedValue = getValues(featureProductDataPath.publishedValue);
    const dataStatus = isEmpty(newValue)
      ? InvestmentDataStatus.MissingData
      : InvestmentDataStatus.Filled;
    setValue(featureProductDataPath.dataStatus, dataStatus);
    setValue(featurePath.dataStatus, dataStatus);
    setValue(featurePath.isModified, !isEqual(newValue, originalTitle));
    setValue(
      featureProductDataPath.isModified,
      !isEqual(newValue, originalTitle),
    );
    if (publishedValue !== newValue) {
      setValue(featureProductDataPath.highlightType, HighlightType.Edited);
      setValue(featurePath.highlightType, HighlightType.Edited);
    } else {
      setValue(featureProductDataPath.highlightType, null);
      setValue(featurePath.highlightType, null);
    }
    setValue(modelFieldPath.name, newValue);
    handleUpdateSectionData();
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    dispatch(toggleSaveDraftButtonDisabled(!isAnySectionModified));
    if (!isMounting) {
      handleUpdateModelsTab({
        isAnySectionModified,
        isAnySectionEdited,
        isAnySectionMissingData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAnySectionModified,
    isAnySectionEdited,
    isAnySectionMissingData,
    isMounting,
  ]);

  const renderModelCodeInput = () => {
    if (!isSuperAdmin) return null;

    return (
      <SHStack spacing={0.5}>
        <Controller
          control={control}
          name={modelFieldPath.modelCode}
          render={({ field: { ref, ...other } }) => (
            <SHNumericFieldV2
              disabled={isSaving || isPendingApproval}
              max={99}
              allowNegative={false}
              valueIsNumericString
              allowLeadingZeros
              maxLength={EditProductDefaults.MaxSeriesCodeLength}
              value={toString(getValues(modelFieldPath.modelCode))}
              sx={{
                "& .MuiInput-input": {
                  fontSize: "24px",
                  fontWeight: "600",
                  fontFamily: "Clash Display",
                  padding: "0px !important",
                  paddingTop: "2px !important",
                  width: "100px",
                },
              }}
              onValueChange={({ value }) => {
                const valueOrNull = value.length === 0 ? null : value;
                other.onChange(valueOrNull);
              }}
            />
          )}
        />
        <SHTypography variant="body3" sx={{ color: palette.text.third }}>
          Model code
        </SHTypography>
      </SHStack>
    );
  };

  return (
    <>
      <SHStack direction={"row"} width={"100%"} spacing={2}>
        <Controller
          control={control}
          name={featureProductDataPath.value}
          render={({ field: { ref, ...other } }) => (
            <SHTextField
              disabled={isSaving || isPendingApproval}
              maxLength={100}
              value={getValues(featureProductDataPath.value)}
              sx={{
                "& .MuiInput-input": {
                  fontSize: "24px",
                  fontWeight: "600",
                  fontFamily: "Clash Display",
                  padding: "0px !important",
                  paddingTop: "2px !important",
                  width: "243px",
                },
              }}
              onChange={(event) => {
                other.onChange(event.target.value);
                handleModelTitleChange(event.target.value);
              }}
            />
          )}
        />
        {renderModelCodeInput()}
      </SHStack>
      <SHDivider sx={{ height: "1px", width: "100%" }} />
    </>
  );
};
